import { useEffect, useState } from "react";
import casinoService from "../../services/casinoService";
import logger from "../utils/logger";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Fetch the first-time purchase offer (1st purchase bonus) for dynamic UI.
 * Returns { offer, loading, error }.
 */
export default function useFirstPurchaseOffer() {
  const { isLoggedIn } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Don't fetch offers if user is not logged in
    if (!isLoggedIn) {
      setOffer(null);
      setLoading(false);
      setError(null);
      return;
    }

    let mounted = true;

    const fetchFirstTimeOffer = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try filtered fetch first
        const resp = await casinoService.getOffers({
          limit: 10,
          featured: false,
          filterType: "first_time",
        });
        // Backend returns { data: [...], meta: {...} }
        const list = Array.isArray(resp?.data) ? resp.data : [];

        // Fallback: if filter isn't supported, fetch general and find by name/badge
        let target = list.find(
          (o) =>
            o?.badge_type === "first_time_bonus" ||
            (o?.name &&
              (o.name.toLowerCase().includes("1st purchase") ||
                o.name.toLowerCase().includes("first time"))),
        );

        if (!target) {
          try {
            const allResp = await casinoService.getOffers({
              limit: 50,
              featured: false,
            });
            const all = Array.isArray(allResp?.data)
              ? allResp.data
              : Array.isArray(allResp)
                ? allResp
                : [];
            target = all.find(
              (o) =>
                o?.badge_type === "first_time_bonus" ||
                (o?.name &&
                  (o.name.toLowerCase().includes("1st purchase") ||
                    o.name.toLowerCase().includes("first time"))),
            );
          } catch (e) {
            // ignore fallback failure
          }
        }

        if (mounted) setOffer(target || null);
      } catch (err) {
        logger.error("useFirstPurchaseOffer: failed to fetch", err);
        if (mounted) setError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchFirstTimeOffer();
    return () => {
      mounted = false;
    };
  }, [isLoggedIn]);

  return { offer, loading, error };
}
